import classNames from 'classnames';
import { useId, useMemo, useRef, useState } from 'react';

import { useResizeObserver } from '@revelio/core';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import { getFormatter, useChartTheme } from '../../utilities';
import { getPrimaryColor } from '../../utilities/plot-colors';
import { plotTestId } from '../../utilities/plotTestId';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import { PlotTooltip } from '../plot-tooltip/plot-tooltip';
import styles from './sankey-chart.module.css';
import type { SankeyValueScale } from './sankey-layout';
import { SankeyLinkTooltipContent } from './sankey-link-tooltip';
import { NodeLabel } from './sankey-node-label';
import { SankeyNodeTooltipContent } from './sankey-node-tooltip';
import type { SankeyData, SankeyRenderLink } from './types';
import { useSankeyLayout } from './use-sankey-layout';

const DEFAULT_NODE_COLOR = 'var(--ui-text-normal, #2d426a)';

export type SankeyChartProps = {
  data: SankeyData | null;
  color?: string;
  nodeColor?: string;
  showNodeLabels?: boolean;
  showNodeValues?: boolean;
  /** Compress link widths for layout legibility; tooltips keep raw values. */
  valueScale?: SankeyValueScale;
  /** Re-space columns to fill the plot height and spread unbalanced ribbons. */
  fillColumnHeight?: boolean;
} & CommonPlotProps;

const format = getFormatter(FormatType.INTEGER);

type HoveredLink = { key: string; group?: string };

const linkDomId = (instanceId: string, index: number): string =>
  `${instanceId}-link-${index}`;

const isLinkHighlighted = (
  link: SankeyRenderLink,
  hovered: HoveredLink | null
): boolean => {
  if (hovered === null) return false;
  if (hovered.group !== undefined) return link.group === hovered.group;
  return link.key === hovered.key;
};

/** Multi-level Sankey driven entirely by graph topology. */
export const SankeyChart = ({
  data,
  color: colorProp,
  nodeColor: nodeColorProp,
  showNodeLabels = true,
  showNodeValues = false,
  valueScale = 'linear',
  fillColumnHeight = true,
  loading = false,
  className,
}: SankeyChartProps) => {
  const { theme, isNewTheme } = useChartTheme();
  const isDarkTheme = theme === 'dark';
  const defaultNodeColor = isDarkTheme ? '#e2e8f0' : DEFAULT_NODE_COLOR;
  const nodeColor = nodeColorProp ?? defaultNodeColor;
  const color = colorProp ?? getPrimaryColor();
  const containerRef = useRef<HTMLDivElement>(null);
  const { width, height } = useResizeObserver(containerRef);
  // Sanitised (no colons) so it is a valid id / `#` fragment for `<use>`.
  const instanceId = useId().replace(/:/g, '');

  const [hovered, setHovered] = useState<HoveredLink | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  const hasData = !!data && data.nodes.length > 0 && data.links.length > 0;

  const {
    isPlotSizeValid,
    maxDepth,
    nodesValidated,
    renderLinks,
    rawByNode,
    interiorLabelMaxWidth,
    sideLabelMaxWidth,
  } = useSankeyLayout({ data, width, height, valueScale, fillColumnHeight });

  // Base ribbons: hover-independent and memoized; highlight lives in the overlay.
  const renderedLinkPaths = useMemo(
    () =>
      renderLinks.map((link) => (
        <path
          key={link.key}
          id={linkDomId(instanceId, link.index)}
          d={link.path}
          fill="none"
          stroke={link.color ?? color}
          strokeWidth={link.width}
          className={styles.path}
          data-testid="sankey-chart__path"
          onMouseEnter={() => setHovered({ key: link.key, group: link.group })}
          onMouseLeave={() => setHovered(null)}
        />
      )),
    [renderLinks, color, instanceId]
  );

  const renderedNodes = useMemo(
    () =>
      nodesValidated.map((node) => {
        const isFirstColumn = node.depth === 0;
        const isLastColumn = node.depth === maxDepth;
        const centerY = (node.y0 + node.y1) / 2;
        const raw = rawByNode.get(node.id) ?? { in: 0, out: 0 };
        const rawTotal = Math.max(raw.in, raw.out);
        return (
          <g key={node.id}>
            {/* New theme renders nodes as empty space (transparent but still
                hoverable for the node tooltip); legacy keeps the filled rect. */}
            <rect
              x={node.x0}
              y={node.y0}
              width={node.x1 - node.x0}
              height={Math.max(node.y1 - node.y0, 1)}
              rx={1}
              fill={isNewTheme ? 'transparent' : (node.color ?? nodeColor)}
              className={styles.node}
              onMouseEnter={() => setHoveredNodeId(node.id)}
              onMouseLeave={() => setHoveredNodeId(null)}
            />
            {showNodeLabels && (
              <NodeLabel
                node={node}
                centerY={centerY}
                isFirstColumn={isFirstColumn}
                isLastColumn={isLastColumn}
                sideLabelMaxWidth={sideLabelMaxWidth}
                interiorLabelMaxWidth={interiorLabelMaxWidth}
                label={node.label ?? node.id}
                value={showNodeValues ? format(rawTotal) : undefined}
              />
            )}
          </g>
        );
      }),
    [
      nodesValidated,
      maxDepth,
      rawByNode,
      nodeColor,
      isNewTheme,
      showNodeLabels,
      showNodeValues,
      sideLabelMaxWidth,
      interiorLabelMaxWidth,
    ]
  );

  // Hover overlay: only this re-renders as the pointer moves.
  const highlightedLinks = useMemo(
    () => renderLinks.filter((link) => isLinkHighlighted(link, hovered)),
    [renderLinks, hovered]
  );
  const hoveredLink = hovered
    ? renderLinks.find((link) => link.key === hovered.key)
    : undefined;
  const hoveredNode = hoveredNodeId
    ? nodesValidated.find((node) => node.id === hoveredNodeId)
    : undefined;

  // One lightweight tooltip anchored at whichever ribbon/node is hovered.
  const tooltip = (() => {
    if (hoveredLink) {
      return {
        x: hoveredLink.anchorX,
        y: hoveredLink.anchorY,
        content: (
          <SankeyLinkTooltipContent link={hoveredLink} format={format} />
        ),
      };
    }
    if (hoveredNode) {
      return {
        x: (hoveredNode.x0 + hoveredNode.x1) / 2,
        y: (hoveredNode.y0 + hoveredNode.y1) / 2,
        content: (
          <SankeyNodeTooltipContent
            node={hoveredNode}
            format={format}
            raw={rawByNode.get(hoveredNode.id) ?? { in: 0, out: 0 }}
          />
        ),
      };
    }
    return null;
  })();

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, className)}
      data-testid={plotTestId(ReplotChartNames.Sankey)}
    >
      <PlotLoadWrapper loading={loading} noData={!hasData}>
        <svg width="100%" height="100%">
          {isPlotSizeValid && (
            <g id="render-container">
              {/* New theme desaturates the non-hovered ribbons while one is
                  hovered; the highlight overlay below stays in full color. */}
              <g
                id="links"
                className={
                  isNewTheme && hovered !== null
                    ? styles.linksDimmed
                    : undefined
                }
              >
                {renderedLinkPaths}
              </g>
              <g id="nodes">{renderedNodes}</g>
              {/* `<use>` re-references existing paths so hovering stays cheap. */}
              <g id="sankey-hover-layer" style={{ pointerEvents: 'none' }}>
                {highlightedLinks.map((link) => (
                  <use
                    key={link.key}
                    href={`#${linkDomId(instanceId, link.index)}`}
                    className={styles.pathHover}
                  />
                ))}
              </g>
            </g>
          )}
        </svg>
      </PlotLoadWrapper>
      {tooltip && (
        <PlotTooltip isVisible x={tooltip.x} y={tooltip.y}>
          {tooltip.content}
        </PlotTooltip>
      )}
    </div>
  );
};
